/**
 * E2E Session Store - Store encrypted E2E chat sessions per identity
 * Each identity has its own set of E2E sessions with different peers
 */

import type { UserId } from '@maco144/shared';

export interface E2ESession {
  peerId: UserId; // Primary key
  sharedSecret: Uint8Array;
  nonce: number;
  isInitiator: boolean;
  createdAt: number;
  lastUsedAt: number;
}

export class E2ESessionStore {
  private userId: UserId;
  private dbName: string;
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor(userId: UserId) {
    this.userId = userId;
    this.dbName = `rising-sun-e2e-sessions-${userId}`;
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('sessions')) {
          const store = db.createObjectStore('sessions', { keyPath: 'peerId' });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('lastUsedAt', 'lastUsedAt', { unique: false });
        }
      };
    });
  }

  private getStore(mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction('sessions', mode);
    return tx.objectStore('sessions');
  }

  /**
   * Save or update a session
   */
  async save(session: E2ESession): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').put(session);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a session with a specific peer
   */
  async get(peerId: UserId): Promise<E2ESession | null> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readonly').get(peerId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * List all sessions, sorted by lastUsedAt descending
   */
  async listSessions(): Promise<E2ESession[]> {
    return new Promise((resolve, reject) => {
      const results: E2ESession[] = [];
      const request = this.getStore('readonly').openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          results.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update last used timestamp
   */
  async updateLastUsed(peerId: UserId): Promise<void> {
    const session = await this.get(peerId);
    if (session) {
      return new Promise((resolve, reject) => {
        const request = this.getStore('readwrite').put({
          ...session,
          lastUsedAt: Date.now(),
        });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  }

  /**
   * Delete a session
   */
  async delete(peerId: UserId): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').delete(peerId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all sessions (when switching away from identity)
   */
  async clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    this.db?.close();
    this.db = null;
  }

  /**
   * Export all sessions (for data migration)
   */
  async export(): Promise<E2ESession[]> {
    return new Promise((resolve, reject) => {
      const results: E2ESession[] = [];
      const request = this.getStore('readonly').openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Import sessions (for data migration)
   */
  async import(sessions: E2ESession[]): Promise<number> {
    // Decide which sessions are new BEFORE opening the write transaction.
    // Awaiting a read (this.get opens its own transaction) inside a readwrite
    // transaction lets that transaction auto-commit, so a subsequent put()
    // would throw TransactionInactiveError.
    const toInsert: E2ESession[] = [];
    for (const session of sessions) {
      const existing = await this.get(session.peerId);
      if (!existing) toInsert.push(session);
    }
    if (toInsert.length === 0) return 0;

    // All put() calls are issued synchronously on one live transaction.
    const store = this.getStore('readwrite');
    await Promise.all(
      toInsert.map(
        (session) =>
          new Promise<void>((resolve, reject) => {
            const request = store.put(session);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          })
      )
    );

    return toInsert.length;
  }
}
